import { Injectable } from '@angular/core';
import { TestListService, LmapService } from '@nrwl/common-lib';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { Papa } from 'ngx-papaparse';
@Injectable({
  providedIn: 'root'
})
export class TwampFlowExportService {
  _scheduleId: string;
  //eslint-disable-next-line
  _testConfig: any;
  private readonly _unsubscribe: Subject<void> = new Subject();
  constructor(
    private readonly testListService: TestListService,
    private lmapService: LmapService,
    private papa: Papa
  ) {}

  exportBtfTwampFlows(testId: string): void {
    this._scheduleId = testId;
    this.testListService
      .getBtfFlowExport(this._scheduleId)
      .pipe(takeUntil(this._unsubscribe))
      .subscribe(response => {
        this.processTWAMPConfig(response?.config);
      });
  }

  //eslint-disable-next-line
  processTWAMPConfig(testConfig: any): void {
    let lmapConfig = null;
    const exportTestConfig = [];
    let twampFullflowsConfig = [];
    let twampLightflowsConfig = [];
    const testObj = {};
    let testName = 'twampTestConfig';
    if (testConfig) {
      const primaryAgentInfo = this.lmapService.getAgentInfo('primary', testConfig);
      if (primaryAgentInfo) {
        const testPointName = primaryAgentInfo.id;
        lmapConfig = this.lmapService.getMATestConfigByName(testConfig, primaryAgentInfo.config);
        if (lmapConfig) {
          const testSchedule = this.lmapService.extractScheduleByTaskName(
            lmapConfig,
            'twamp-initiator'
          );
          testName = testSchedule.name;
          let pmInterval = null;
          const pmResultSchedule = this.lmapService.extractReportScheduleByType(lmapConfig, 'pm');
          if (pmResultSchedule && pmResultSchedule.start) {
            const pmIntObj = this.lmapService.extractEventByName(
              lmapConfig,
              pmResultSchedule.start
            );
            if (pmIntObj && pmIntObj.periodic && typeof pmIntObj.periodic.interval === 'number') {
              pmInterval = parseInt(pmIntObj.periodic.interval);
            }
          }
          if (testSchedule) {
            // Source Info
            // get the source info from the first flow
            // since we don't know if we have TWAMP Full flows, Light Flows or both, check for both

            // First check for TWAMP Full flows
            // get first twamp-session-request within first twamp-client-ctrl-connection within twamp-client
            let clientConnectionArray = null;

            let srcIp = null;
            let startPort = null;
            const codecAnalysis = this.lmapService.extractScheduleOptionByName(
              testSchedule,
              'codec'
            );

            const persist = this.lmapService.extractScheduleOptionByName(testSchedule, 'persist');

            const percentilesVal = this.lmapService.extractScheduleOptionByName(
              testSchedule,
              'enable-percentiles'
            );

            const qtPortId = this.lmapService.extractScheduleOptionByName(testSchedule, 'phy-id');
            const keepAlive = this.lmapService.extractScheduleOptionByName(
              testSchedule,
              'twamp-keep-alive-interval'
            );

            let map = null;
            let fullOptions = this.lmapService.extractScheduleOptionByName(testSchedule, 'twamp');
            if (fullOptions) {
              map = this.lmapService.extractScheduleOptionByName(testSchedule, 'test-session-map');
              if (map) {
                map = JSON.parse(map);
              }
              fullOptions = JSON.parse(fullOptions);
              if (
                fullOptions['ietf-twamp:twamp'] &&
                fullOptions['ietf-twamp:twamp']['twamp-client'] &&
                fullOptions['ietf-twamp:twamp']['twamp-client']['twamp-client-ctrl-connection']
              ) {
                clientConnectionArray =
                  fullOptions['ietf-twamp:twamp']['twamp-client']['twamp-client-ctrl-connection'];
                if (clientConnectionArray && clientConnectionArray.length > 0) {
                  if (
                    clientConnectionArray[0]['twamp-session-request'] &&
                    clientConnectionArray[0]['twamp-session-request'].length > 0
                  ) {
                    srcIp = clientConnectionArray[0]['twamp-session-request'][0]['sender-ip'];
                    startPort =
                      clientConnectionArray[0]['twamp-session-request'][0]['sender-udp-port'];
                  }
                }
              }
            }

            // Now check for TWAMP Light flows
            let lightOptions = this.lmapService.extractScheduleOptionByName(
              testSchedule,
              'twamp-light'
            );
            let lightFlowArray = null;
            if (lightOptions) {
              map = this.lmapService.extractScheduleOptionByName(testSchedule, 'session-map');
              if (map) {
                map = JSON.parse(map);
              }
              lightOptions = JSON.parse(lightOptions);

              if (
                lightOptions['ietf-twamp-light:twamp-light'] &&
                lightOptions['ietf-twamp-light:twamp-light']['twamp-light-session-sender'] &&
                lightOptions['ietf-twamp-light:twamp-light']['twamp-light-session-sender'][
                  'test-session'
                ] &&
                lightOptions['ietf-twamp-light:twamp-light']['twamp-light-session-sender'][
                  'test-session'
                ].length
              ) {
                lightFlowArray =
                  lightOptions['ietf-twamp-light:twamp-light']['twamp-light-session-sender'][
                    'test-session'
                  ];
              }
            }

            if (srcIp === null && startPort === null) {
              if (lightFlowArray && lightFlowArray.length && lightFlowArray.length > 0) {
                if (lightFlowArray[0]['sender-ip']) {
                  srcIp = lightFlowArray[0]['sender-ip'];
                }
                if (lightFlowArray[0]['sender-udp-port']) {
                  startPort = lightFlowArray[0]['sender-udp-port'];
                }
              }
            }

            const subnetPrefixLength = this.lmapService.extractScheduleOptionByName(
              testSchedule,
              'prefix-length'
            );
            const subnet = this.lmapService.extractScheduleOptionByName(testSchedule, 'subnet');
            const gateway = this.lmapService.extractScheduleOptionByName(testSchedule, 'gateway');
            const vlanID = this.lmapService.extractScheduleOptionByName(testSchedule, 'vlan-id');
            const cVlanID = this.lmapService.extractScheduleOptionByName(
              testSchedule,
              'inner-vlan-id'
            );
            const etherType = this.lmapService.extractScheduleOptionByName(testSchedule, 'tpid');
            const sVlanPriority =
              this.lmapService.extractScheduleOptionByName(testSchedule, 'vlan-priority') || '0';
            const cVlanPriority =
              this.lmapService.extractScheduleOptionByName(testSchedule, 'inner-vlan-priority') ||
              '0';

            testObj['T/TEST'] = 'T';
            testObj['TEST NAME'] = testName;
            testObj['MA NAME'] = testPointName;
            testObj['IP ADDRESS'] = srcIp;
            testObj['GATEWAY'] = gateway;
            testObj['MASK'] = subnet;
            testObj['PREFIX LENGTH'] = subnetPrefixLength;
            testObj['VLAN'] = vlanID || 0;
            testObj['VLAN PRIORITY'] = sVlanPriority || 0;
            testObj['CVLAN'] = cVlanID || 0;
            testObj['CVLAN PRIORITY'] = cVlanPriority || 0;
            testObj['TPID'] = etherType;
            testObj['KEEP ALIVE'] = keepAlive?.toLowerCase() === 'true' ? 'YES' : 'NO';
            testObj['PM REPORT INTERVAL'] = pmInterval;
            testObj['PERSIST'] = persist?.toLowerCase() === 'true' ? 'YES' : 'NO';
            testObj['PERCENTILES'] = percentilesVal?.toLowerCase() === 'true' ? 'YES' : 'NO';
            testObj['CODEC'] = codecAnalysis ? codecAnalysis : 'none';
            testObj['STARTING UDP PORT'] = startPort;
            testObj['QT PORT'] = qtPortId ? (qtPortId?.toLowerCase() == 'p1' ? '1' : '2') : '0';

            if (fullOptions && clientConnectionArray) {
              twampFullflowsConfig = this.getConfigFullFlows(
                fullOptions,
                clientConnectionArray,
                map
              );
            }

            if (lightFlowArray) {
              twampLightflowsConfig = this.getConfigLightFlows(lightFlowArray, map);
            }
          }
        }
      }
    }

    exportTestConfig.push(testObj);
    const configuredFlows = [...twampFullflowsConfig, ...twampLightflowsConfig];
    // Combine the sections manually
    const combinedData = [
      this.papa.unparse(exportTestConfig, { header: true }),
      [], // Blank row to separate sections
      this.papa.unparse(configuredFlows, { header: true })
    ];

    // Join the rows into a single CSV string
    const csvString = combinedData.join('\n');
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = testName + '.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  //eslint-disable-next-line
  getConfigFullFlows(fullOptions: any, clientConnectionArray: any, map: any): any {
    const flowsConfig = [];
    // get twamp-client-ctrl-connection array
    if (clientConnectionArray) {
      // get twamp-sender-test-session array
      let senderTestSessionArray = null;
      if (
        fullOptions &&
        fullOptions['ietf-twamp:twamp']['twamp-client']?.['twamp-session-sender'][
          'twamp-sender-test-session'
        ]?.length
      ) {
        senderTestSessionArray =
          fullOptions['ietf-twamp:twamp']['twamp-client']['twamp-session-sender'][
            'twamp-sender-test-session'
          ];
      }

      for (const x in clientConnectionArray) {
        if (
          clientConnectionArray[x]['twamp-session-request'] &&
          clientConnectionArray[x]['twamp-session-request'].length > 0
        ) {
          for (const y in clientConnectionArray[x]['twamp-session-request']) {
            const flowsObj = {};
            let dfBit = false;
            const fid = clientConnectionArray[x]['twamp-session-request'][y]['test-session-name'];
            const destIP =
              clientConnectionArray[x]['twamp-session-request'][y]['reflector-ip'] || '';

            const dscp = clientConnectionArray[x]['twamp-session-request'][y]['dscp'];

            const reflectorUdpPort =
              clientConnectionArray[x]['twamp-session-request'][y]['reflector-udp-port'] || '';

            let frameSize = clientConnectionArray[x]['twamp-session-request'][y]['padding-length'];
            if (typeof frameSize !== 'undefined') {
              const padLen = parseInt(frameSize);
              if (!isNaN(padLen)) {
                frameSize = padLen + 87;
              } else {
                frameSize = '';
              }
            } else {
              frameSize = '';
            }

            let txPeriod = '';
            for (const z in senderTestSessionArray) {
              if (senderTestSessionArray[z]['test-session-name'] === fid) {
                txPeriod = senderTestSessionArray[z]['periodic-interval'];
                break;
              }
            }

            if (map && map?.[fid]?.['df-bit']) {
              dfBit = map?.[fid]?.['df-bit'];
            }

            let controlServerIp = null;
            const controlServerPort = clientConnectionArray[x]?.['server-tcp-port'];

            if (clientConnectionArray[x]['server-ip']) {
              controlServerIp = clientConnectionArray[x]['server-ip'];
            }
            if (!controlServerIp) controlServerIp = '';

            flowsObj['F/FLOW'] = 'F';
            flowsObj['FLOW NAME'] = fid;
            flowsObj['TWAMP LIGHT'] = 'NO';
            flowsObj['TX PERIOD'] = txPeriod;
            flowsObj['FRAME SIZE'] = frameSize;
            flowsObj['REFLECTOR IP ADDRESS'] = destIP;
            flowsObj['REFLECTOR UDP PORT'] = reflectorUdpPort;
            flowsObj['DSCP'] = dscp;
            flowsObj['PAYLOAD TYPE'] = '';
            flowsObj['CONTROL SERVER TCP PORT'] = controlServerPort || '';
            flowsObj['DF BIT'] = dfBit;
            flowsObj['REFLECTOR TYPE'] = 'full';
            flowsConfig.push(flowsObj);
          }
        }
      }
    }
    return flowsConfig;
  }

  //eslint-disable-next-line
  getConfigLightFlows(lightFlowArray: any, map: any): any {
    const flowsConfig = [];
    if (lightFlowArray) {
      for (const z in lightFlowArray) {
        const flowsObj = {};
        let fid = lightFlowArray[z]['session-id'];
        let sessionType = 'TWAMP Light';
        let dfBit = false;
        let payLoadType = 0;

        // look name up in map if provided
        if (map) {
          for (const entry in map) {
            if (entry === fid.toString() && map[entry].name) {
              fid = map[entry].name;
              sessionType = map[entry]['payload-type'] === 1 ? 'Ping' : 'TWAMP Light';
              dfBit = map[entry]?.['df-bit'];
              payLoadType = map[entry]['payload-type'];
              break;
            }
          }
        }

        const destIP = lightFlowArray[z]['reflector-ip'] || '';
        const dscp = lightFlowArray[z]['dscp'];

        const reflectorUdpPort =
          sessionType === 'Ping' ? '' : lightFlowArray[z]['reflector-udp-port'];
        let frameSize = lightFlowArray[z]['packet-padding-size'];
        if (typeof frameSize !== 'undefined') {
          const padLen = parseInt(frameSize);
          if (!isNaN(padLen)) {
            frameSize = padLen + 87;
          } else {
            frameSize = '';
          }
        } else {
          frameSize = '';
        }

        const txPeriod = lightFlowArray[z]['interval'] || '';

        flowsObj['F/FLOW'] = 'F';
        flowsObj['FLOW NAME'] = fid;
        flowsObj['TWAMP LIGHT'] = 'Yes';
        flowsObj['TX PERIOD'] = txPeriod;
        flowsObj['FRAME SIZE'] = frameSize;
        flowsObj['REFLECTOR IP ADDRESS'] = destIP;
        flowsObj['REFLECTOR UDP PORT'] = reflectorUdpPort || '';
        flowsObj['DSCP'] = dscp;
        flowsObj['PAYLOAD TYPE'] = payLoadType;
        flowsObj['CONTROL SERVER TCP PORT'] = 'NO';
        flowsObj['DF BIT'] = dfBit;
        flowsObj['REFLECTOR TYPE'] = sessionType === 'Ping' ? 'ping' : 'light';
        flowsConfig.push(flowsObj);
      }
    }
    return flowsConfig;
  }

  dispose(): void {
    this._unsubscribe.next();
    this._unsubscribe.complete();
  }
}
